import {
  GridColDef,
  GridComparatorFn,
  GridSortModel,
  GridValidRowModel,
  gridStringOrNumberComparator,
} from '@mui/x-data-grid-premium';

import dayjs from 'dayjs';

import { GeneratikTableColumn } from './GeneratikTable.types';
import { FilterFieldConfig, FilterValues } from './GeneratikTableFilterPanel.types';
import { FilterChip } from './GeneratikTableHeader.types';

const UNGROUPED_KEY = '__ungrouped__';

export const resolveGroupValue = <R extends GridValidRowModel>(
  row: R,
  field: string,
  columns: GeneratikTableColumn<R>[],
  groupValueGetter?: (row: R, field: string) => string | number | null | undefined,
): string => {
  if (groupValueGetter) {
    const value = groupValueGetter(row, field);
    if (value === null || value === undefined || value === '') return UNGROUPED_KEY;
    return String(value);
  }

  const column = columns.find((col) => col.field === field);
  const rawValue = field.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as object)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, row);

  if (column?.groupingValueGetter) {
    try {
      const computed = (column.groupingValueGetter as unknown as (...args: unknown[]) => unknown)(
        rawValue,
        row,
        column,
        undefined,
      );
      if (computed === null || computed === undefined || computed === '') return UNGROUPED_KEY;
      return String(computed);
    } catch {
      // fall through to valueGetter / rawValue
    }
  }

  if (column?.valueGetter) {
    try {
      const computed = (column.valueGetter as unknown as (...args: unknown[]) => unknown)(
        rawValue,
        row,
        column,
        undefined,
      );
      if (computed === null || computed === undefined || computed === '') return UNGROUPED_KEY;
      return String(computed);
    } catch {
      // fall through to rawValue
    }
  }

  if (rawValue === null || rawValue === undefined || rawValue === '') return UNGROUPED_KEY;
  return String(rawValue);
};

export const isUngroupedKey = (value: string | number): boolean => String(value) === UNGROUPED_KEY;

export const sumGroupItemPrice = <R>(
  rows: R[],
  getGroupItemPrice: (row: R) => number | undefined,
): number => rows.reduce((sum, row) => sum + (getGroupItemPrice(row) ?? 0), 0);

export const buildFilterChips = (
  values: FilterValues,
  fields: FilterFieldConfig[],
): FilterChip[] => {
  const chips: FilterChip[] = [];

  Object.entries(values).forEach(([key, value]) => {
    const field = fields.find((f) => f.id === key);
    if (!field) return;

    const removable = field.removable;

    if (typeof value === 'string' && value.trim()) {
      const label = field.options?.find((o) => o.value === value)?.label || value;
      chips.push({
        id: key,
        label: field.hideChipLabel ? '' : field.label,
        value: label,
        removable,
      });
    } else if (Array.isArray(value) && value.length > 0) {
      const labels = value
        .map((v) => field.options?.find((o) => o.value === v)?.label || v)
        .join(', ');
      chips.push({ id: key, label: field.label, value: labels, removable });
    } else if (typeof value === 'object' && value !== null) {
      const rangeValue = value as { start?: string; end?: string; min?: string; max?: string };
      if (rangeValue.start || rangeValue.end) {
        const parts = [];
        if (rangeValue.start) parts.push(dayjs(rangeValue.start).format('DD.MM.YYYY'));
        if (rangeValue.end) parts.push(dayjs(rangeValue.end).format('DD.MM.YYYY'));
        chips.push({ id: key, label: field.label, value: parts.join(' - '), removable });
      } else if (rangeValue.min || rangeValue.max) {
        const parts = [];
        if (rangeValue.min) parts.push(rangeValue.min);
        if (rangeValue.max) parts.push(rangeValue.max);
        chips.push({ id: key, label: field.label, value: parts.join(' - '), removable });
      }
    }
  });

  return chips;
};

export function dedupeRowsById<R>(rows: R[], getRowId: (row: R) => string | number): R[] {
  const seen = new Set<string | number>();
  const duplicateIds: Array<string | number> = [];
  const result: R[] = [];

  for (const row of rows) {
    const id = getRowId(row);
    if (seen.has(id)) {
      duplicateIds.push(id);
      continue;
    }
    seen.add(id);
    result.push(row);
  }

  if (duplicateIds.length === 0) return rows;

  if (import.meta.env.DEV) {
    console.warn(
      `GeneratikTable: dropped ${duplicateIds.length} row(s) with duplicate id: ${duplicateIds.join(', ')}`,
    );
  }

  return result;
}

export function sortRowsBySortModel<R extends GridValidRowModel>(
  rows: R[],
  sortModel: GridSortModel,
  columns: GridColDef<R>[],
): R[] {
  if (!sortModel.length) return rows;

  const sortItems = sortModel
    .map((item) => {
      const column = columns.find((col) => col.field === item.field);
      if (!column || !item.sort) return null;
      const comparator: GridComparatorFn = column.getSortComparator
        ? column.getSortComparator(item.sort)
        : (() => {
            const base = column.sortComparator ?? gridStringOrNumberComparator;
            const directionFactor = item.sort === 'desc' ? -1 : 1;
            return (v1, v2, p1, p2) => directionFactor * base(v1, v2, p1, p2);
          })();
      return { column, comparator };
    })
    .filter((entry): entry is { column: GridColDef<R>; comparator: GridComparatorFn } => !!entry);

  if (!sortItems.length) return rows;

  const getValue = (row: R, column: GridColDef<R>): unknown => {
    const rawValue = (row as Record<string, unknown>)[column.field];
    if (!column.valueGetter) return rawValue;
    try {
      return (column.valueGetter as unknown as (...args: unknown[]) => unknown)(
        rawValue,
        row,
        column,
        undefined,
      );
    } catch {
      return rawValue;
    }
  };

  const decorated = rows.map((row) => ({
    row,
    values: sortItems.map(({ column }) => getValue(row, column)),
  }));

  decorated.sort((a, b) => {
    for (let i = 0; i < sortItems.length; i++) {
      const result = sortItems[i].comparator(
        a.values[i],
        b.values[i],
        undefined as never,
        undefined as never,
      );
      if (result !== 0) return result;
    }
    return 0;
  });

  return decorated.map((d) => d.row);
}

export { formatCurrency, formatDate, formatNumber } from '@/shared/lib/format';
